import React, { useContext, useState } from "react";
import { Card, OverlayTrigger, Popover, Tooltip } from "react-bootstrap";
import { ChevronDown, ChevronRight, ChevronUp } from "react-feather";

import { UserSettingsContext } from "../contexts/UserSettingsContext";
import rawPsvTypes from "./psvTypes";
import LastActivityDisplay from "./LastActivityDisplay";
import TaskStatusBadge from "./TaskStatusBadge";
import TaskModal from "./TaskModal";

// Building blocks shared by the Open tasks and Waiting cards

// Lists refetch when tasksRefresh (UserSettingsContext) changes; rows bump it when a task is saved/deleted from the modal

export const ellipsis = { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };

const chipStyle = { fontSize: "11px", lineHeight: "16px", padding: "1px 8px", borderRadius: "999px", whiteSpace: "nowrap" };

// Same short label and colors as the Task card's status badge
export const StatusChip = ({ status }) => <TaskStatusBadge status={status} />;

export const OutlineChip = ({ children }) => (
  <span style={{ ...chipStyle, background: "#fff", color: "#5A6163", border: "1px solid #DCE5E3" }}>{children}</span>
);

export const CountPill = ({ count }) => (
  <span style={{ fontSize: "12px", background: "#D9E5E2", color: "#2F4F4A", borderRadius: "999px", padding: "0 8px", flexShrink: 0 }}>{count}</span>
);

export const linkButtonStyle = { fontSize: "13px", color: "#3E8A80", textDecoration: "none" };

// Outline shared by the "newer" cards (tasks, enrollment matrix, summary tiles)
export const cardOutlineStyle = { border: "1px solid #DCE5E3", borderRadius: "2px" };

// collapsible adds a chevron before the title that hides the body and footer (header stays visible)
export const TaskCard = ({ title, count, headerChip, headerRight, footer, className, collapsible, children }) => {
  const [expanded, setExpanded] = useState(true);
  const open = !collapsible || expanded;
  const titleEl = <span style={{ fontSize: "15px", fontWeight: 500, color: "#3F4446", whiteSpace: "nowrap" }}>{title}</span>;
  return (
    <Card className={className} style={{ ...cardOutlineStyle, overflow: "hidden" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "8px", padding: collapsible ? "12px 16px 12px 8px" : "12px 16px" }}>
        {collapsible ? (
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            aria-expanded={expanded}
            aria-label={(expanded ? "Collapse " : "Expand ") + (typeof title === "string" ? title : "card")}
            style={{ display: "flex", alignItems: "center", gap: "4px", background: "none", border: 0, padding: 0, textAlign: "left", color: "inherit", cursor: "pointer", flexShrink: 0 }}
          >
            <ChevronDown size={14} style={{ color: "#8a929a", transition: "transform 0.2s", transform: expanded ? "none" : "rotate(-90deg)" }} />
            {titleEl}
          </button>
        ) : titleEl}
        <CountPill count={count} />
        {headerChip}
        {headerRight && <div className="ms-auto">{headerRight}</div>}
      </div>
      {open && children}
      {open && footer && <div style={{ borderTop: "1px solid #EEF2F1", padding: "10px 16px" }}>{footer}</div>}
    </Card>
  );
};

// Chevron points down for descending, up for ascending
export const SortButton = ({ label, ascending, onToggle, ascTitle, descTitle }) => (
  <button type="button" className="btn btn-link p-0 d-inline-flex align-items-center" style={{ ...linkButtonStyle, gap: "2px", whiteSpace: "nowrap" }}
    onClick={onToggle} title={ascending ? ascTitle : descTitle}>
    {label} {ascending ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
  </button>
);

export const AssigneeAvatars = ({ assignees, hover }) => (
  <div style={{ display: "flex" }}>
    {assignees.map((a, i) => {
      const name = a.firstName + " " + a.lastName;
      const hasPhoto = a.photoUrl != null && a.photoUrl !== "";
      const initials = ((a.firstName || "").charAt(0) + (a.lastName || "").charAt(0)).toUpperCase();
      return (
        <OverlayTrigger key={i} placement="top" overlay={<Tooltip>{name}</Tooltip>}>
          <span style={{ width: "24px", height: "24px", borderRadius: "50%", background: "#3E8A80", color: "#fff", overflow: "hidden",
            fontSize: "10.5px", fontWeight: 500, display: "inline-flex", alignItems: "center", justifyContent: "center",
            boxSizing: "content-box", border: "2px solid " + (hover ? "#EEF5F3" : "#fff"), marginLeft: i === 0 ? 0 : "-8px" }}>
            {hasPhoto ? <img src={a.photoUrl} alt={name} width="24" height="24" style={{ objectFit: "cover" }} /> : initials}
          </span>
        </OverlayTrigger>
      );
    })}
  </div>
);

// One clickable row: headline, optional lines under it, right-hand content, optional chips below.
// Clicking opens the task modal; hovering shows the most recent update.
export const TaskRow = ({ task, headline, headlineStyle, lines, right, chips }) => {
  const [hover, setHover] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const { tasksRefresh, setTasksRefresh } = useContext(UserSettingsContext);
  const notifyTasksChanged = () => setTasksRefresh(tasksRefresh + 1);

  const hasActivity = task.activity && task.activity.length > 0 && task.activity[0].notes;

  const row = (
    <div
      id={task.id}
      onClick={() => setShowModal(true)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{ position: "relative", padding: "11px 22px 11px 16px", cursor: "pointer", borderTop: "1px solid #EEF2F1", background: hover ? "#EEF5F3" : "transparent" }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
        <div style={{ flex: 1, minWidth: 0, fontSize: "14px", fontWeight: 500, color: "#2F7469", ...ellipsis, ...headlineStyle }}>{headline}</div>
        <div style={{ display: "flex", alignItems: "center", gap: "10px", flexShrink: 0, minHeight: "21px" }}>
          {typeof right === "function" ? right(hover) : right}
        </div>
      </div>
      {lines}
      <ChevronRight size={16} color="#8A9496" style={{ position: "absolute", top: "14px", right: "4px", opacity: hover ? 1 : 0, transition: "opacity 0.15s" }} />
      {chips && <div style={{ display: "flex", gap: "6px", marginTop: "6px", flexWrap: "wrap" }}>{chips}</div>}
    </div>
  );

  const modal = showModal && (
    <TaskModal show={true} additionalInstructionsEntityName={task.additionalInstructionsEntityName} practiceName={task.practiceName} onCancel={() => setShowModal(false)} onSave={() => { setShowModal(false); notifyTasksChanged(); }}
      onRefresh={notifyTasksChanged} onDelete={() => { setShowModal(false); notifyTasksChanged(); }}
      id={task.id} category={task.category?.value} psvType={task.psvType} state={task.state} practiceId={task.practiceId} carrierId={task.carrierId}
      providerId={task.providerIsDeleted ? 0 : task.providerId} hubId={task.hubId} facilityId={task.facilityId} other={task.other} referenceNumber={task.referenceNumber}
      assignees={task.assignees} status={task.status} due={task.due} deadline={task.deadline} archived={task.archived} tracked={task.tracked} activityList={task.activity}
      providerIsDeleted={task.providerIsDeleted} eSignatureTokenInfo={task.eSignatureTokenInfo} documentTokenInfo={task.documentTokenInfo} />
  );

  // The modal is a sibling of the row so clicks inside it don't bubble up to the row's onClick
  if (!hasActivity) return <>{row}{modal}</>;

  return (
    <>
      <OverlayTrigger
        placement="bottom"
        delay={{ show: 250, hide: 0 }}
        overlay={
          <Popover style={{ maxWidth: "420px" }}>
            <Popover.Body style={{ paddingBottom: 0 }}>
              <LastActivityDisplay task={task} />
            </Popover.Body>
          </Popover>
        }
      >
        {row}
      </OverlayTrigger>
      {modal}
    </>
  );
};

// The standard task row used by both cards. `metric` is the card-specific value shown beside the
// avatars (due date, days waiting).
export const TaskSummaryRow = ({ task, entityType, metric }) => {
  const psvType = rawPsvTypes.find((t) => t.value === task.psvType);
  const psvDisplay = psvType ? psvType.label + (task.state ? " | " + task.state : "") : "";

  const title = (task.carrierId !== 0 && task.carrierName) || (task.facilityId !== 0 && task.facilityName)
    || (task.hubId !== 0 && task.hubName) || psvDisplay || task.category?.label || "";

  const textParts = [];
  if (task.other) textParts.push(task.other);
  if (title !== psvDisplay && psvDisplay) textParts.push(psvDisplay);

  const linkStyle = { color: "#495057" };
  let entityLine = null;
  if (entityType === "practice" && task.providerId !== 0 && task.providerFormattedName) {
    entityLine = task.providerIsDeleted
      ? task.providerFormattedName
      : <a href={"/provider/" + task.providerId} style={linkStyle} onClick={(e) => e.stopPropagation()}>{task.providerFormattedName}</a>;
  } else if (entityType === "provider" && task.practiceName) {
    entityLine = <a href={"/practice/" + task.practiceId} style={linkStyle} onClick={(e) => e.stopPropagation()}>{task.practiceName}</a>;
  }

  const assignees = task.assignees || [];

  return (
    <TaskRow
      task={task}
      headline={title}
      lines={<>
        {textParts.length > 0 && <div style={{ fontSize: "12.5px", color: "#6B7274", ...ellipsis }}>{textParts.join(" · ")}</div>}
        {entityLine && <div style={{ fontSize: "12.5px", color: "#495057", ...ellipsis }}>{entityLine}</div>}
      </>}
      right={(hover) => <>
        {metric}
        {assignees.length > 0 && <AssigneeAvatars assignees={assignees} hover={hover} />}
      </>}
      chips={<>
        <StatusChip status={task.status} />
        {task.category?.label && <OutlineChip>{task.category.label}</OutlineChip>}
      </>}
    />
  );
};
